import { useCallback, useEffect, useRef } from 'react';
import { ESRI_MAP_DEFAULT_ZOOM_LEVEL, ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL } from '../configurations';
import { areLatLngsEqual } from '../../location/helpers';
import { GeoPoint } from '../../location/types';
import { MapView, TimestampedGeoPoint } from '../types';
import { useMapControls } from './use-map-controls';
import { moveLayerOnTop } from '../helpers/utils';
import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { WebMap } from '../constructors';
import { SHORTCUTS_BY_CODE } from '../constants';
import { useUserActionTracker } from '../../monitoring/user-action-tracker/use-user-action-tracker';

interface Props {
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
    defaultZoomLevel?: number;
    entityViewZoomLevel?: number;
    mapView?: MapView;
    webMap?: WebMap;
    defaultCenter?: GeoPoint;
    isFollowModeEnabled: boolean;
    setIsFollowModeEnabled: (isEnabled: boolean) => void;
    selectedEventLocation?: TimestampedGeoPoint;
    selectedUnitLocation?: TimestampedGeoPoint;
}

export const useKeyboardShortcut = ({
    mapRef,
    entityViewZoomLevel = ESRI_MAP_VIEW_ENTITY_ZOOM_LEVEL,
    defaultZoomLevel = ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    mapView,
    webMap,
    defaultCenter,
    isFollowModeEnabled,
    setIsFollowModeEnabled,
    selectedEventLocation,
    selectedUnitLocation,
}: Props) => {
    const prevUnitLocationRef = useRef<TimestampedGeoPoint | null | undefined>(
        selectedUnitLocation
    );
    const prevIsFollowModeEnabledRef = useRef(isFollowModeEnabled);

    const { resetMap } = useMapControls({
        mapView,
        defaultCenter,
        entityViewZoomLevel,
        defaultZoomLevel,
        isFollowModeEnabled,
        setIsFollowModeEnabled,
    });
    const handleClickRef = useRef<(e: KeyboardEvent) => void>();

    const userActionTracker = useUserActionTracker();

    useEffect(() => {
        prevUnitLocationRef.current = selectedUnitLocation;
        prevIsFollowModeEnabledRef.current = isFollowModeEnabled;
        const currSelectedUnitLocation = selectedUnitLocation;
        const prevSelectedUnitLocation = prevUnitLocationRef.current;

        const shouldTrackUnit = isFollowModeEnabled && !!selectedUnitLocation;
        const isUnitLocationNew = !areLatLngsEqual(
            currSelectedUnitLocation!,
            prevSelectedUnitLocation!
        );
        const didLocationModeSwitchOn = isFollowModeEnabled && prevIsFollowModeEnabledRef;

        if (shouldTrackUnit && (isUnitLocationNew || didLocationModeSwitchOn)) {
            if (!currSelectedUnitLocation || !mapView) {
                return;
            }
            mapView
                .goTo(
                    {
                        center: [
                            currSelectedUnitLocation.longitude,
                            currSelectedUnitLocation.latitude,
                        ],
                    },
                    { animate: false }
                )
                .catch(() => {});
        }
    }, [selectedUnitLocation, isFollowModeEnabled, mapView]);

    const handleClick = useCallback(
        (e: KeyboardEvent) => {
            if (!mapView) return;
            switch (e.code) {
                case 'Digit1':
                    if (!selectedUnitLocation) break;
                    isFollowModeEnabled
                        ? setIsFollowModeEnabled(false)
                        : setIsFollowModeEnabled(true);
                    break;
                case 'Digit2':
                    if (!selectedEventLocation) break;
                    setIsFollowModeEnabled(false);
                    mapView
                        .goTo(
                            {
                                center: [
                                    selectedEventLocation.longitude,
                                    selectedEventLocation.latitude,
                                ],
                                zoom: entityViewZoomLevel,
                            },
                            { animate: false }
                        )
                        .catch(() => {});
                    break;
                case 'Digit3':
                    setIsFollowModeEnabled(false);
                    resetMap();
                    break;
                case 'Digit0':
                    setIsFollowModeEnabled(false);
                    mapView
                        .goTo({
                            zoom: defaultZoomLevel,
                        })
                        .catch(() => {});
                    break;
                case 'Minus':
                    setIsFollowModeEnabled(false);
                    break;
                case 'Equal':
                    setIsFollowModeEnabled(false);
                    break;
                case 'KeyE':
                    moveLayerOnTop(webMap, FEATURE_LAYERS_PROPERTIES.EVENTS.layerKey);
                    break;
                case 'KeyU':
                    moveLayerOnTop(webMap, FEATURE_LAYERS_PROPERTIES.UNITS.layerKey);
                    break;
                default:
            }
            userActionTracker?.trackMapShortcut({
                mapShortcutKey: e.code,
                mapShortcutText: SHORTCUTS_BY_CODE[e.code]?.text,
            });
            return;
        },
        [
            mapView,
            webMap,
            entityViewZoomLevel,
            defaultZoomLevel,
            resetMap,
            selectedEventLocation,
            isFollowModeEnabled,
            selectedUnitLocation,
            setIsFollowModeEnabled,
            userActionTracker,
        ]
    );

    useEffect(() => {
        const refCurrent = mapRef.current;
        if (!refCurrent) return;

        const handler = (e: KeyboardEvent) => handleClick(e);
        if (handleClickRef.current) {
            refCurrent.removeEventListener('keydown', handleClickRef.current);
        }

        refCurrent.addEventListener('keydown', handler);
        handleClickRef.current = handler;

        return () => {
            if (!refCurrent) return;
            if (handleClickRef.current) {
                refCurrent.removeEventListener('keydown', handleClickRef.current);
            }
        };
    }, [mapRef, handleClick]);
};
